import { NotesContentViewer } from "@client/components/round/allocator/MarkdownViewer";
import { NoteHistoryButton } from "@client/components/round/NoteHistoryModal";
import { RichTextEditor } from "@client/components/utils/RichTextEditor";
import { useVisitedNavItems } from "@client/hooks/use-visited-nav-items";
import {
  useActingAsUserId,
  useDispatchWithResult,
  useRound,
  useUserProfiles,
  useViewerTrack,
} from "@client/pages/round/round-context";
import { toast } from "sonner";
import {
  isRichTextBlank,
  richTextToPlainText,
} from "@client/utils/isRichTextBlank";
import { getRecommenderFromUser } from "@engine/round/accessors";
import { trackSchema } from "@lib/types";
import { canViewBasedOnTrack, redacted } from "@lib/utils/anonymization";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@sfc/ui";
import { NotebookPenIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useRecordTodoObservation } from "@client/pages/round/funder-workspace/todo/useTodoObservation";
import { todoStepIds } from "@lib/todoRail/todoStepIds";
import {
  GENERAL_NOTES_NAV_KEY,
  openGeneralNotes,
  setGeneralNotesOpen,
  useGeneralNotes,
} from "./generalNotesStore";

type AutosaveStatus = "unsaved" | "saved" | null;

// Sentinel selector value for the viewer's own (editable) note, distinct from
// any real member id.
const SELF_VALUE = "__self__";

type VisibleRecommender = {
  memberId: string;
  name: string;
  /** null when the recommender has no (visible) note yet — shown as "(no note)". */
  note: string | null;
};

/**
 * Recommenders whose general note the current viewer is allowed to see, by the
 * same rule the engine applies to general notes (`anonymizeAggregate`):
 * funders/admins (NO_TRACK) see everyone; otherwise the note is visible when
 * it's same-track OR the author's feedback is visible by default. Members the
 * viewer can't see are excluded (their note also arrives as the `redacted`
 * sentinel). Members with no note yet are still listed (`note: null`) so the
 * selector can show "(no note)", matching the prototype. Excludes the viewer
 * themselves — they appear as the editable "My general notes" entry.
 */
function useVisibleRecommenders(): VisibleRecommender[] {
  const round = useRound();
  const userProfiles = useUserProfiles();
  const viewerTrack = useViewerTrack();
  const actingAsUserId = useActingAsUserId();
  return useMemo(() => {
    const items: VisibleRecommender[] = [];
    for (const member of Object.values(round.members)) {
      if (member.id === actingAsUserId) continue; // self = "My general notes"
      if (member.status !== "active") continue;
      if (!getRecommenderFromUser(round, member.id)) continue;
      const canSee =
        canViewBasedOnTrack(viewerTrack, trackSchema.parse(member.track)) ||
        member.feedbackVisibleByDefault;
      if (!canSee) continue;
      const raw = member.generalNotes;
      const note =
        raw == null || raw === redacted || isRichTextBlank(raw) ? null : raw;
      items.push({
        memberId: member.id,
        name: userProfiles?.[member.id]?.name ?? member.initials,
        note,
      });
    }
    items.sort((a, b) => a.name.localeCompare(b.name));
    return items;
  }, [round, userProfiles, viewerTrack, actingAsUserId]);
}

/**
 * Unified "General round notes" drawer (SP-2152 / SP-2153). A "Viewing" selector
 * switches between the viewer's own note ("My general notes", editable for
 * active recommenders) and every other recommender they're allowed to see
 * (read-only, including "(no note)" entries). Mounted once by `Round.tsx`; the
 * triggers live outside the round provider and flip `generalNotesStore`.
 */
export function GeneralNotesDrawer() {
  const round = useRound();
  const actingAsUserId = useActingAsUserId();
  const dispatchWithResult = useDispatchWithResult();
  // `resetOnUnmount` clears the flag on navigation so it can't leak across rounds.
  const { open, memberId } = useGeneralNotes({ resetOnUnmount: true });
  const [autosave, setAutosave] = useState<AutosaveStatus>(null);
  const { markVisited } = useVisitedNavItems(round.id);

  const others = useVisibleRecommenders();

  const selfMember = actingAsUserId ? round.members[actingAsUserId] : undefined;
  const isRecommender =
    actingAsUserId != null && !!getRecommenderFromUser(round, actingAsUserId);
  const canEditSelf =
    !!selfMember && selfMember.status === "active" && isRecommender;

  // Clear the header icon's "unseen" dot the first time the drawer opens, from
  // any trigger.
  useEffect(() => {
    if (open) markVisited(GENERAL_NOTES_NAV_KEY);
  }, [open, markVisited]);

  // The trigger's `memberId` sets the initial focus (`null` = the viewer's own
  // note). Resolve the displayed entry as `selectedValue ?? initialValue` and
  // clear the manual override whenever the drawer (re)opens or the target
  // changes, so reopening always lands where the trigger intended (mirrors the
  // pattern previously used by the browse drawer).
  const initialValue =
    memberId != null && memberId !== actingAsUserId
      ? memberId
      : canEditSelf
        ? SELF_VALUE
        : (others[0]?.memberId ?? null);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    initialValue,
  );
  const [prevKey, setPrevKey] = useState(`${open}|${memberId}`);
  const key = `${open}|${memberId}`;
  if (key !== prevKey) {
    setPrevKey(key);
    if (open) setSelectedValue(initialValue);
  }

  const options: { value: string; label: string }[] = [
    ...(canEditSelf ? [{ value: SELF_VALUE, label: "My general notes" }] : []),
    ...others.map((o) => ({
      value: o.memberId,
      label: o.note == null ? `${o.name} (no note)` : o.name,
    })),
  ];

  const effectiveValue = selectedValue ?? options[0]?.value ?? null;
  const showingSelf = effectiveValue === SELF_VALUE;
  const selectedOther =
    !showingSelf && effectiveValue != null
      ? (others.find((o) => o.memberId === effectiveValue) ?? null)
      : null;
  const historyMemberId = showingSelf
    ? (selfMember?.id ?? null)
    : (selectedOther?.memberId ?? null);
  const historyLabel = showingSelf
    ? "your general round notes"
    : `${selectedOther?.name ?? "recommender"}'s general round notes`;

  // Reading a recommender's round notes here is the To Do rail's "read general
  // round notes" mark — the rail records what the app observed rather than
  // asking the funder to tick it. Only the other-recommender case counts: the
  // viewer's own note is not something they need to be told to read. The hook's
  // dwell applies, so flicking through the "Viewing" selector marks nothing.
  useRecordTodoObservation(
    round.id,
    // `selectedOther.note` is null for a recommender with no note — the
    // selector even labels them "(no note)". Recording one anyway wrote a mark
    // for a step the rail doesn't show yet, so when that recommender finally
    // wrote their notes the step appeared ALREADY COMPLETE and the funder was
    // never shown them.
    open && selectedOther?.note != null
      ? (() => {
          const recommender = getRecommenderFromUser(
            round,
            selectedOther.memberId,
          );
          return recommender
            ? todoStepIds.recommenderNotes(recommender.id)
            : null;
        })()
      : null,
    // This surface lives inside the notes drawer, so that drawer being open is
    // what makes the notes readable — not something covering them.
    { hostDrawer: "generalNotes" },
  );

  // Self note may be locked once the viewer's track is complete — proposal
  // notes go read-only then (see NoteEditor), and the round note is the same
  // funder-visible rationale, so lock it the same way. Notes stay editable
  // through evals-freeze.
  const selfTrack = selfMember
    ? trackSchema.parse(selfMember.track)
    : undefined;
  const selfTrackStatus =
    selfTrack == null || selfTrack === "NO_TRACK"
      ? undefined
      : round.trackSettings[selfTrack].status;
  const isTrackComplete = selfTrackStatus === "5.complete";

  async function handleChange(notes: string) {
    if (!selfMember) return;
    // Propagate failure so the editor's autosave/conflict flows can keep the
    // "Saved" label honest and hold a conflict open (dispatchAndIgnore would
    // toast and resolve either way). Still toast, as before.
    const error = await dispatchWithResult({
      type: "GENERAL_NOTES_CHANGE",
      memberId: selfMember.id,
      notes,
    });
    if (error) {
      // `dispatch` already toasts for read-only mode — don't double up.
      if (!error.message.includes("read-only mode")) {
        toast.error("Couldn't save your note: " + error.message);
      }
      throw error;
    }
  }

  return (
    <Sheet open={open} onOpenChange={setGeneralNotesOpen}>
      <SheetContent side="right" className="w-95 sm:max-w-105">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <NotebookPenIcon className="size-5" />
            General round notes
            {showingSelf && autosave != null && (
              <span className="text-body-sm font-normal text-muted-foreground">
                {autosave === "saved" ? "Saved" : "Unsaved changes"}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-1 pt-2 pb-4">
          <p className="text-body-sm text-muted-foreground">
            Round-level notes that aren&apos;t tied to any single proposal — a
            recommender&apos;s overall philosophy for the round, how they
            weighed things, and caveats that apply across all their evaluations.
            Visible to the funder.
          </p>

          {options.length === 0 ? (
            <p className="text-body-sm text-text-muted">
              No general round notes to show.
            </p>
          ) : (
            <>
              {options.length > 1 ? (
                <div className="flex items-center gap-1">
                  <Select
                    value={effectiveValue ?? undefined}
                    onValueChange={setSelectedValue}
                  >
                    <SelectTrigger className="min-w-0 flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {historyMemberId && (
                    <NoteHistoryButton
                      target={{
                        roundId: round.id,
                        memberId: historyMemberId,
                        evaluableId: null,
                        field: "general",
                      }}
                      noteLabel={historyLabel}
                    />
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <p className="text-label-md text-foreground">
                    {options[0].label}
                  </p>
                  {historyMemberId && (
                    <NoteHistoryButton
                      target={{
                        roundId: round.id,
                        memberId: historyMemberId,
                        evaluableId: null,
                        field: "general",
                      }}
                      noteLabel={historyLabel}
                    />
                  )}
                </div>
              )}

              {showingSelf ? (
                isTrackComplete ? (
                  <>
                    <p className="text-body-sm italic text-text-muted">
                      Notes are read-only because the round is complete.
                    </p>
                    {isRichTextBlank(selfMember?.generalNotes) ? (
                      <p className="text-body-sm text-text-muted">
                        No general notes.
                      </p>
                    ) : (
                      <NotesContentViewer
                        value={selfMember?.generalNotes ?? ""}
                      />
                    )}
                  </>
                ) : (
                  <RichTextEditor
                    value={selfMember?.generalNotes ?? ""}
                    setValue={handleChange}
                    placeholder="Your overall thoughts on this round…"
                    aria-label="General round notes"
                    onAutosaveChange={setAutosave}
                  />
                )
              ) : selectedOther?.note != null ? (
                <div className="text-body-md text-foreground">
                  <NotesContentViewer value={selectedOther.note} />
                </div>
              ) : (
                <p className="text-body-sm text-text-muted">
                  No general notes.
                </p>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Card on the recommender's proposals page that opens the drawer to their own
 * (editable) note, with a live preview of what they've written.
 */
export function GeneralNotesAffordance() {
  const round = useRound();
  const actingAsUserId = useActingAsUserId();
  const member = actingAsUserId ? round.members[actingAsUserId] : undefined;
  const isRecommender =
    actingAsUserId != null && getRecommenderFromUser(round, actingAsUserId);
  if (!member || member.status !== "active" || !isRecommender) return null;

  const hasNote = !isRichTextBlank(member.generalNotes);
  const preview = hasNote ? richTextToPlainText(member.generalNotes) : null;

  return (
    <div className="mb-4 flex items-start gap-3 rounded-lg border border-border-subtle bg-surface-base px-4 py-3.5">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-elevated text-text-secondary">
        <NotebookPenIcon className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <span className="block text-label-md text-foreground">
          Your general round notes
        </span>
        {preview != null ? (
          <p className="mt-0.5 line-clamp-2 text-body-sm text-muted-foreground">
            {preview}
          </p>
        ) : (
          <p className="mt-0.5 text-body-sm text-muted-foreground">
            Round-level notes for the funder, not tied to any proposal — your
            overall philosophy, how you weighed things, and caveats that apply
            across all your evaluations.
          </p>
        )}
        <span className="mt-1 block text-body-sm text-text-muted">
          Open it anytime from the notebook icon in the top bar.
        </span>
      </div>
      <button
        type="button"
        onClick={() => openGeneralNotes(null)}
        className="shrink-0 rounded-md border border-border-subtle px-3 py-1.5 text-label-md text-foreground transition-colors hover:bg-surface-hover"
      >
        {hasNote ? "Edit my note" : "Add a note"}
      </button>
    </div>
  );
}

/**
 * Affordance beneath a recommender's proposal note: opens the drawer focused on
 * that recommender. Renders only when that recommender has a general note the
 * viewer can see.
 */
export function GeneralNotesBrowseAffordance({
  memberId,
}: {
  memberId: string;
}) {
  const round = useRound();
  const member = round.members[memberId];
  const note = member?.generalNotes;
  if (!member || member.status !== "active") return null;
  if (!getRecommenderFromUser(round, member.id)) return null;
  if (isRichTextBlank(note) || note === redacted) return null;

  return (
    <button
      type="button"
      onClick={() => openGeneralNotes(memberId)}
      className="flex items-center gap-2 text-label-md text-text-secondary transition-colors hover:text-accent"
    >
      <NotebookPenIcon className="size-4 shrink-0" />
      General round notes
    </button>
  );
}
